import * as React from "react";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import Modal from "../../misc/components/modal";
import { parse } from "csv/browser/esm";
import { FileDownload } from "../../misc/FileDownload";

interface ImportSitesProps {
    onClose: () => void;
}

export const ImportSites = ({ onClose }: ImportSitesProps) => {
    const [selectedFile, setSelectedFile] = React.useState<File>();
    const [fileDelimiter, setFileDelimiter] = React.useState<string>(",");
    const client = useWebPortalApiClient();
    const [error, setError] = React.useState<string | undefined>();
    const requiredMessage = "Please select a CSV file";

    const onDownloadTemplate = async () => {
        const dataTemplate = "SiteId,Site Name,Site Address,Site Email,Site Phone Number,Site Fax Number,Study Guid,Site Guid,Country,TimeZone,Contact";

        const blobData = new Blob([dataTemplate], { type: "text/csv" });

        FileDownload(blobData, "SiteImportTemplate.csv", "text/csv");
    }

    const onFileChange = (event) => {
        const file = event.target.files[0];
        if (!file || file.type !== 'text/csv') {
            setError(requiredMessage);
            setSelectedFile(null);
            event.target.value = null;
            return;
        }
        setSelectedFile(file);
        setError(null);
    }

    const onSaveClick = async () => {
        setError(undefined);
        const reader = new FileReader();

        if (fileDelimiter === "" || fileDelimiter === null || fileDelimiter === undefined) {
            setError("File delimiter is required, default is ',' (comma).");
            return;
        }

        reader.onload = async (event) => {
            const file = event.target.result as string;

            parse(file, {
                delimiter: fileDelimiter,
                columns: ["Site Id", "Site Name", "Site Address", "Site Email", "Site Phone Number", "Site Fax Number", "Study Guid", "Site Guid", "Country", "TimeZone", "Contact"],
                from_line: 2
            }, async (err, data) => {
                if (err) {
                    setError(err.message);
                    return;
                }

                try {
                    for (const record of data) {
                        await client.client.postSites({
                            id: record["Site Guid"],
                            number: record["Site Id"],
                            name: record["Site Name"],
                            address: record["Site Address"],
                            email: record["Site Email"],
                            phone: record["Site Phone Number"],
                            fax: record["Site Fax Number"],
                            studyId: record["Study Guid"],
                            country: record["Country"],
                            timeZone: record["TimeZone"],
                            contact: record["Contact"]
                        });
                    }

                    onClose();
                } catch (ex) {
                    setError(ex.message);
                }
            });
        };

        reader.readAsText(selectedFile);
    }

    return (
        <Modal show={true} header="Import Sites from CSV" closeModalHandler={onClose}>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel-body">
                        {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
                        <div className="form-group col-md-6">
                            <label htmlFor="sitesFile">CSV File</label>
                            <input id="sitesFile" type="file" className="form-control" onChange={onFileChange} />
                        </div>
                        <div className="form-group col-md-6">
                            <label htmlFor="delimiter">File Delimiter</label>
                            <input id="delimiter" type="text" className="form-control" minLength={0} maxLength={1} required value={fileDelimiter} onChange={(e) => setFileDelimiter(e.target.value)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button id="importButton" className="btn btn-primary" type="submit" disabled={!selectedFile} onClick={onSaveClick}>
                    Import
                </button>&nbsp;
                <button id="downloadTemplateButton" className="btn btn-primary btn-outline" onClick={() => onDownloadTemplate()}>
                    Download Template
                </button>
                <button id="returnToListButton" className="btn btn-primary btn-outline" onClick={onClose}>
                    Return to List
                </button>
            </div>
        </Modal>
    );
}
